import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ListChecks, Key, Trophy, FileUp, FileText } from "lucide-react";
import { QUIZ_ICONS, QUIZ_ICON_OPTIONS, darkenHex } from "../../lib/quizIcons";
import ColorField from "../../components/ColorField";

const EMPTY = { name_pt: "", name_en: "", type: "training", active: true, order: 0, global_time_seconds: 300, question_order: "created", pdf_path: "", icon: "play", icon_color: "", price_eur: 0, price_gbp: 0, ranking_enabled: true, ranking_top_n: 10 };

export default function AdminQuizzes() {
  const [quizzes, setQuizzes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    const { data } = await api.get("/admin/quizzes");
    setQuizzes(data || []);
  };
  useEffect(() => { load(); }, []);

  const uploadPdf = async (file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, pdf_path: data.path }));
      toast.success("PDF carregado");
    } catch (e) { toast.error(e.response?.data?.detail || "Upload falhou"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await api.put(`/admin/quizzes/${editingId}`, form);
        toast.success("Quiz atualizado");
      } else {
        await api.post("/admin/quizzes", form);
        toast.success("Quiz criado");
      }
      setForm(EMPTY);
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro");
    }
  };

  const edit = (q) => {
    setEditingId(q.id);
    setForm({ ...EMPTY, ...q });
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminar este quiz e todas as suas perguntas/credenciais/resultados?")) return;
    await api.delete(`/admin/quizzes/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div data-testid="admin-quizzes-page">
      <h1 className="font-outfit text-3xl font-bold text-[#0B1D3A] mb-6">Quizzes</h1>

      <form onSubmit={submit} className="rounded-2xl bg-white p-5 border border-[#0B1D3A]/5 mb-6 space-y-3" data-testid="quiz-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <input placeholder="Nome (PT)" value={form.name_pt} onChange={(e) => setForm({ ...form, name_pt: e.target.value })} required className="px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-name-pt-input" />
          <input placeholder="Nome (BR) — opcional" value={form.name_br || ""} onChange={(e) => setForm({ ...form, name_br: e.target.value })} className="px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-name-br-input" />
          <input placeholder="Name (EN)" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} required className="px-3 py-2 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-name-en-input" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="text-xs font-semibold text-[#0B1D3A]/70">Tipo
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal" data-testid="quiz-type-select">
              <option value="training">Treino</option>
              <option value="competition">Concurso</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-[#0B1D3A]/70">Tempo total (s)
            <input type="number" min="10" value={form.global_time_seconds} onChange={(e) => setForm({ ...form, global_time_seconds: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal" data-testid="quiz-global-time-input" />
          </label>
          <label className="text-xs font-semibold text-[#0B1D3A]/70">Ordem das perguntas
            <select value={form.question_order} onChange={(e) => setForm({ ...form, question_order: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal" data-testid="quiz-question-order-select">
              <option value="created">Por ordem de criação</option>
              <option value="random">Aleatório</option>
            </select>
          </label>
          <label className="text-xs font-semibold text-[#0B1D3A]/70">Ordem (apresentação)
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal" data-testid="quiz-order-input" />
          </label>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
          <label className="text-xs font-semibold text-[#0B1D3A]/70">Preço (EUR)
            <input type="number" step="0.01" min="0" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: parseFloat(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal" data-testid="quiz-price-eur-input" />
          </label>
          <label className="text-xs font-semibold text-[#0B1D3A]/70">PDF da prova
            <div className="flex items-center gap-2 mt-1">
              <input type="file" accept="application/pdf" onChange={(e) => e.target.files?.[0] && uploadPdf(e.target.files[0])} className="text-xs font-normal" data-testid="quiz-pdf-input" />
              {form.pdf_path && <a href={`${backendUrl}/api/files/${form.pdf_path}`} target="_blank" rel="noreferrer" className="text-xs text-[#005F73] underline whitespace-nowrap">Ver</a>}
            </div>
          </label>
          <label className="flex items-center gap-2 text-xs font-semibold text-[#0B1D3A]/70 pb-2">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="quiz-active-check" /> Ativo
          </label>
        </div>
        {form.type === "competition" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-center rounded-xl border border-[#0B1D3A]/10 p-3" data-testid="quiz-ranking-config">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#0B1D3A]/70">
              <input type="checkbox" checked={form.ranking_enabled !== false} onChange={(e) => setForm({ ...form, ranking_enabled: e.target.checked })} data-testid="quiz-ranking-enabled-check" /> Ranking público ativo (para este concurso)
            </label>
            <label className="text-xs font-semibold text-[#0B1D3A]/70">Nº de alunos no ranking
              <select value={String(form.ranking_top_n ?? 10)} disabled={form.ranking_enabled === false} onChange={(e) => setForm({ ...form, ranking_top_n: parseInt(e.target.value, 10) })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[#0B1D3A]/15 font-normal disabled:opacity-50" data-testid="quiz-ranking-topn-select">
                <option value="10">Top 10</option>
                <option value="20">Top 20</option>
                <option value="50">Top 50</option>
                <option value="100">Top 100</option>
                <option value="0">Todos</option>
              </select>
            </label>
          </div>
        )}
        <div className="rounded-xl border border-[#0B1D3A]/10 p-3 space-y-2" data-testid="quiz-icon-config">
          <p className="text-xs font-semibold text-[#0B1D3A]/70">Ícone do botão (cada prova pode ter um ícone e cor diferentes)</p>
          <div className="flex flex-wrap items-center gap-2">
            {QUIZ_ICON_OPTIONS.map((name) => {
              const Ic = QUIZ_ICONS[name];
              const selected = (form.icon || "play") === name;
              return (
                <button key={name} type="button" onClick={() => setForm({ ...form, icon: name })} data-testid={`quiz-icon-${name}`}
                  className={`h-10 w-10 inline-flex items-center justify-center rounded-xl border-2 transition ${selected ? "border-[#FF6B6B] bg-[#FF6B6B]/10" : "border-[#0B1D3A]/10 hover:border-[#0B1D3A]/30"}`}>
                  <Ic size={18} className="text-[#0B1D3A]" />
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 pt-1">
            <label className="flex items-center gap-2 text-xs font-semibold text-[#0B1D3A]/70">Cor do ícone
              <ColorField value={form.icon_color || "#FF6B6B"} onChange={(e) => setForm({ ...form, icon_color: e.target.value })} className="h-9 w-14 rounded-lg border border-[#0B1D3A]/15" data-testid="quiz-icon-color" />
            </label>
            {form.icon_color && <button type="button" onClick={() => setForm({ ...form, icon_color: "" })} className="text-xs text-[#0B1D3A]/50 underline" data-testid="quiz-icon-color-auto">Cor automática (diferente por botão)</button>}
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl ml-auto" style={{ background: form.icon_color || "#FF6B6B", boxShadow: `0 4px 0 ${darkenHex(form.icon_color || "#FF6B6B")}` }} data-testid="quiz-icon-preview">
              {(() => { const Ic = QUIZ_ICONS[form.icon || "play"]; return <Ic size={18} className="text-white" />; })()}
            </span>
          </div>
          <p className="text-[11px] text-[#0B1D3A]/45">Deixe a cor vazia ("automática") para que cada prova use uma cor distinta da paleta.</p>
        </div>

        <button type="submit" className="bg-[#0B1D3A] text-white rounded-lg font-bold inline-flex items-center justify-center gap-2 px-5 py-2.5" data-testid="quiz-submit-btn">
          <Plus size={16} /> {editingId ? "Atualizar" : "Criar"}
        </button>
      </form>

      <div className="rounded-2xl bg-white border border-[#0B1D3A]/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-[#0B1D3A]/70">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Nome</th>
              <th className="text-left px-4 py-3 font-semibold">Tipo</th>
              <th className="text-left px-4 py-3 font-semibold">Tempo</th>
              <th className="text-left px-4 py-3 font-semibold">Ativo</th>
              <th className="text-left px-4 py-3 font-semibold">Ordem</th>
              <th className="text-left px-4 py-3 font-semibold">PDF</th>
              <th className="text-right px-4 py-3 font-semibold">Ações</th>
            </tr>
          </thead>
          <tbody>
            {quizzes.map((q) => (
              <tr key={q.id} className="border-t border-[#0B1D3A]/5" data-testid={`quiz-row-${q.id}`}>
                <td className="px-4 py-3 font-medium text-[#0B1D3A]">{q.name_pt} <span className="text-[#0B1D3A]/40">/ {q.name_en}</span></td>
                <td className="px-4 py-3">{q.type === "training" ? "Treino" : "Concurso"}</td>
                <td className="px-4 py-3">{q.global_time_seconds ?? 300}s</td>
                <td className="px-4 py-3">{q.active ? "✓" : "✗"}</td>
                <td className="px-4 py-3">{q.order || 0}</td>
                <td className="px-4 py-3">
                  <div className="inline-flex gap-1.5">
                    <a href={`${backendUrl}/api/admin/quizzes/${q.id}/questions-pdf?lang=pt`} download className="px-2 py-1.5 rounded-md bg-[#FF6B6B]/10 text-[#FF6B6B] hover:bg-[#FF6B6B]/20 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`quiz-pdf-pt-${q.id}`}>
                      <FileText size={14} /> PT
                    </a>
                    <a href={`${backendUrl}/api/admin/quizzes/${q.id}/questions-pdf?lang=br`} download className="px-2 py-1.5 rounded-md bg-[#2D9C5A]/10 text-[#2D9C5A] hover:bg-[#2D9C5A]/20 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`quiz-pdf-br-${q.id}`}>
                      <FileText size={14} /> BR
                    </a>
                    <a href={`${backendUrl}/api/admin/quizzes/${q.id}/questions-pdf?lang=en`} download className="px-2 py-1.5 rounded-md bg-[#005F73]/10 text-[#005F73] hover:bg-[#005F73]/20 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`quiz-pdf-en-${q.id}`}>
                      <FileText size={14} /> EN
                    </a>
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <Link to={`/admin/quizzes/${q.id}/questions`} className="px-2 py-1.5 rounded-md bg-[#FF6B6B]/10 text-[#FF6B6B] hover:bg-[#FF6B6B]/20 inline-flex items-center gap-1 text-xs font-semibold">
                      <ListChecks size={14} /> Perguntas
                    </Link>
                    {q.type === "competition" && (
                      <>
                        <Link to={`/admin/quizzes/${q.id}/credentials`} className="px-2 py-1.5 rounded-md bg-[#005F73]/10 text-[#005F73] hover:bg-[#005F73]/20 inline-flex items-center gap-1 text-xs font-semibold">
                          <Key size={14} /> Credenciais
                        </Link>
                        <Link to={`/admin/quizzes/${q.id}/results`} className="px-2 py-1.5 rounded-md bg-[#FFD93D]/30 text-[#856404] hover:bg-[#FFD93D]/50 inline-flex items-center gap-1 text-xs font-semibold">
                          <Trophy size={14} /> Resultados
                        </Link>
                      </>
                    )}
                    <button onClick={() => edit(q)} className="px-2 py-1.5 rounded-md bg-[#0B1D3A]/5 hover:bg-[#0B1D3A]/10 inline-flex items-center gap-1 text-xs"><Edit size={14} /></button>
                    <button onClick={() => remove(q.id)} className="px-2 py-1.5 rounded-md bg-red-50 text-red-600 hover:bg-red-100 inline-flex items-center gap-1 text-xs"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {quizzes.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-[#0B1D3A]/50">Sem quizzes ainda. Cria o primeiro acima!</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
